import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

const DataMigration = () => {
  return (
    <>
      <Helmet>
        <title>Easy CRM Migration to DojoTrack | Martial Arts Studio Software</title>
        <meta
          name="description"
          content="Seamlessly migrate from your existing CRM to DojoTrack. Import students, leads, and payment data with our guided migration tools. Make the switch to the best martial arts studio software."
        />
        <meta
          name="keywords"
          content="CRM migration, martial arts software migration, dojo management software, data import, student management migration"
        />
        <meta name="author" content="DojoTrack LLC" />
        <meta
          property="og:title"
          content="Easy CRM Migration to DojoTrack | Martial Arts Studio Software"
        />
        <meta
          property="og:description"
          content="Seamlessly migrate from your existing CRM to DojoTrack. Import students, leads, and payment data with our guided migration tools."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://dojotrack.com/data-migration" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Easy CRM Migration to DojoTrack | Martial Arts Studio Software"
        />
        <meta
          name="twitter:description"
          content="Seamlessly migrate from your existing CRM to DojoTrack. Import students, leads, and payment data with our guided migration tools."
        />
        <link rel="canonical" href="https://dojotrack.com/data-migration" />
      </Helmet>
      <div className="bg-white dark:bg-gray-900 min-h-screen py-16 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl">
        {/* Hero */}
        <div className="text-center max-w-3xl mx-auto">
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
            <span className="text-gray-900 dark:text-white">Migration From Your Other Platform </span>
            <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">Made Easy</span>
          </h1>
          <p className="mt-6 text-lg leading-8 text-gray-600 dark:text-gray-300">
            Seamlessly bring your existing students, leads, and payment data into DojoTrack. Whether you're switching from another system or just getting started, we make the process simple and stress‑free.
          </p>
        </div>

        {/* Quick Steps */}
        <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm hover:shadow-xl hover:border-indigo-300 dark:hover:border-indigo-600 transition-all duration-300 transform hover:-translate-y-1">
            <div className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">Step 1</div>
            <h3 className="mt-2 text-lg font-semibold text-gray-900 dark:text-white">Export Your Data</h3>
            <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">Export students or leads from your current CRM as <strong>CSV</strong>, <strong>Excel</strong>, or <strong>JSON</strong>.</p>
          </div>
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm hover:shadow-xl hover:border-indigo-300 dark:hover:border-indigo-600 transition-all duration-300 transform hover:-translate-y-1">
            <div className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">Step 2</div>
            <h3 className="mt-2 text-lg font-semibold text-gray-900 dark:text-white">Upload to DojoTrack</h3>
            <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">Use our guided importer with intelligent column matching and a downloadable template to map your fields quickly.</p>
          </div>
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm hover:shadow-xl hover:border-indigo-300 dark:hover:border-indigo-600 transition-all duration-300 transform hover:-translate-y-1">
            <div className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">Step 3</div>
            <h3 className="mt-2 text-lg font-semibold text-gray-900 dark:text-white">Connect Stripe (Optional)</h3>
            <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">Already on Stripe? Import customers and keep payment methods synced.</p>
          </div>
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm hover:shadow-xl hover:border-indigo-300 dark:hover:border-indigo-600 transition-all duration-300 transform hover:-translate-y-1">
            <div className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">Step 4</div>
            <h3 className="mt-2 text-lg font-semibold text-gray-900 dark:text-white">Notify Everyone Once</h3>
            <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">Send a one‑time Email or SMS using the <span className="font-mono">{'{{payment_link}}'}</span> tag so families can update their payment method in minutes.</p>
          </div>
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm hover:shadow-xl hover:border-indigo-300 dark:hover:border-indigo-600 transition-all duration-300 transform hover:-translate-y-1">
            <div className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">Step 5</div>
            <h3 className="mt-2 text-lg font-semibold text-gray-900 dark:text-white">Create Memberships & Start Billing</h3>
            <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">Set up membership plans with pricing, assign them to students, and start collecting monthly tuition automatically through Stripe.</p>
          </div>
        </div>

        {/* One-time Outreach Feature */}
        <section className="mt-20 rounded-2xl border border-indigo-200 dark:border-indigo-900 bg-gradient-to-br from-indigo-50/80 to-purple-50/80 dark:from-indigo-900/30 dark:to-purple-900/30 p-8 sm:p-10 shadow-lg">
          <div className="sm:flex sm:items-start sm:justify-between gap-8">
            <div className="sm:max-w-3xl">
              <h2 className="text-2xl font-bold">
                <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">One‑Time Email/SMS Payment Link Outreach</span>
              </h2>
              <p className="mt-4 text-base text-gray-700 dark:text-gray-300">
                Reach every family at once. From <strong>Messaging</strong> or <strong>SMS</strong>, compose your announcement and insert the <span className="font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{'{{payment_link}}'}</span> tag. Each student automatically receives a secure, student‑specific Stripe portal link to add or update their payment method.
              </p>
              <ul className="mt-6 space-y-3 text-sm text-gray-700 dark:text-gray-300 list-disc pl-5">
                <li>Personalized links—no manual copy/paste</li>
                <li>Works for both Email and SMS in a single send</li>
                <li>Perfect for onboarding or card‑on‑file refreshes</li>
              </ul>
            </div>
            <div className="mt-6 sm:mt-0 w-full sm:w-auto">
              <div className="rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Example message</div>
                <pre className="text-sm text-gray-800 dark:text-gray-200 font-mono whitespace-pre-wrap bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">{`Hello {{student_name}}, this is {{studio_name}}.
Please update your payment method here: {{payment_link}}`}</pre>
              </div>
            </div>
          </div>
        </section>

        {/* Detailed Sections */}
        <section className="mt-20 bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
            <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">Import Students and Leads</span>
          </h3>
          <p className="mt-4 text-lg text-gray-700 dark:text-gray-300">
            Upload a <strong>CSV</strong>, <strong>Excel (XLSX)</strong>, or <strong>JSON</strong> file—no manual data entry required. Our column‑matching wizard guides you to map fields and validates required columns so your roster is ready to use immediately.
          </p>
        </section>

        <section className="mt-8 bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
            <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">Import Stripe Customers</span>
          </h3>
          <p className="mt-4 text-lg text-gray-700 dark:text-gray-300">
            Connect Stripe and pull in your existing customers as students, including saved payment methods and subscription status. Your students can keep using their cards on file—no re‑entry necessary.
          </p>
        </section>

        {/* CTA */}
        <div className="mt-20 flex flex-col items-center">
          <Link
            to="/signup"
            className="rounded-md bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-4 text-lg font-semibold text-white shadow-lg hover:from-indigo-500 hover:to-purple-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 mb-4 transition-all transform hover:scale-105 hover:shadow-xl"
          >
            Start Your Free Trial
          </Link>
          <Link
            to="/contact"
            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300 hover:underline text-base font-medium transition-colors"
          >
            Contact Support
          </Link>
        </div>
      </div>
    </div>
    </>
  );
};

export default DataMigration;
